package top.wcpe.allincore.dsl.compiler.codegen

import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.DrawInstructionAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.codegen.internal.ExprSerializer
import top.wcpe.allincore.dsl.ir.canvas.DrawBlob
import top.wcpe.allincore.dsl.ir.canvas.DrawOpcodes
import top.wcpe.allincore.dsl.ir.canvas.Instruction
import top.wcpe.allincore.dsl.ir.canvas.Operand

/**
 * 将 [DrawAst] 编译为 [DrawBlob] 字节码（M11.6）。
 *
 * 编译规则：
 *  - 图元调用按 opcode 对应位置翻译操作数；text/drawImage 的最后一个参数必须是字符串字面量。
 *  - `val name = expr` 为当前作用域分配一个 slot（0 ~ LOCAL_SLOT_COUNT-1），后续表达式中
 *    引用同名标识符时，在 EXPR 求值期由 DrawInterpreter 以 `$slotN` 绑定；本阶段仅关注
 *    字节码布局与 slot 回收。
 *  - `for (i in from until to)` 的 body 先递归编码为字节数组以计算 bodyByteLength，再装配
 *    FOR_BEGIN + body + FOR_END。
 *  - `if (cond) { then } else { elseBranch }` 同理装配 IF_BEGIN + then + IF_END + else + IF_END。
 *
 * slot 分配策略：
 *  - 使用栈式作用域：`pushScope / popScope` 保存/还原 nextSlot 与变量映射；
 *  - 同一变量名在子作用域内可遮蔽外层声明；退出子作用域后恢复。
 */
class DrawBlobEmitter {

    /** 图元函数名 → opcode 映射。 */
    private val primitiveOpcodes: Map<String, Byte> = mapOf(
        "rect" to DrawBlob.OP_RECT,
        "circle" to DrawBlob.OP_CIRCLE,
        "dot" to DrawBlob.OP_DOT,
        "line" to DrawBlob.OP_LINE,
        "arc" to DrawBlob.OP_ARC,
        "ring" to DrawBlob.OP_RING,
        "roundRect" to DrawBlob.OP_ROUND_RECT,
        "text" to DrawBlob.OP_TEXT,
        "polygon" to DrawBlob.OP_POLYGON,
        "path" to DrawBlob.OP_PATH,
        "drawImage" to DrawBlob.OP_DRAW_IMAGE,
        // v4 B.3：bezier / bezierCubic 走 generic else 分支（8 / 10 个 LITERAL 操作数）。
        "bezier" to DrawBlob.OP_BEZIER_QUAD,
        "bezierCubic" to DrawBlob.OP_BEZIER_CUBIC,
        // v4 B.4：复合路径 opcode；走 buildPathBuild 特殊处理（变长节点序列）。
        "pathBuild" to DrawBlob.OP_PATH_BUILD,
        // v4 B.5：像素位图 opcode；走 buildBitmap 特殊处理（data 为 ListLiteral<NumberLit> 字节字面量）。
        "bitmap" to DrawBlob.OP_BITMAP,
    )

    /** 当前编译上下文的 slot 分配器（toOperand 需要查找变量名→slot 映射）。 */
    private var currentSlots: SlotAllocator? = null

    /** 入口：编译为 DrawBlob 字节数组。 */
    fun emit(drawAst: DrawAst): ByteArray {
        val instructions = mutableListOf<Instruction>()
        val slotAllocator = SlotAllocator()
        currentSlots = slotAllocator
        emitInstructions(drawAst.instructions, instructions, slotAllocator)
        currentSlots = null
        return DrawBlob.encode(instructions)
    }

    /** 递归编码一组指令到 [out]，维护 [slots] 栈式作用域。 */
    private fun emitInstructions(
        ast: List<DrawInstructionAst>,
        out: MutableList<Instruction>,
        slots: SlotAllocator,
    ) {
        for (ins in ast) {
            when (ins) {
                is DrawInstructionAst.Primitive -> emitPrimitive(ins, out)
                is DrawInstructionAst.ValAssign -> emitValAssign(ins, out, slots)
                is DrawInstructionAst.For -> emitFor(ins, out, slots)
                is DrawInstructionAst.IfElse -> emitIfElse(ins, out, slots)
            }
        }
    }

    /** 翻译图元调用。 */
    private fun emitPrimitive(
        ins: DrawInstructionAst.Primitive,
        out: MutableList<Instruction>,
    ) {
        val opcode = primitiveOpcodes[ins.name]
            ?: error("未知图元函数 '${ins.name}'")
        when (opcode) {
            DrawBlob.OP_TEXT -> out += buildText(ins)
            DrawBlob.OP_DRAW_IMAGE -> out += buildDrawImage(ins)
            DrawBlob.OP_POLYGON -> out += buildPolygon(ins)
            DrawBlob.OP_PATH -> out += buildPath(ins)
            DrawBlob.OP_PATH_BUILD -> out += buildPathBuild(ins)
            DrawBlob.OP_BITMAP -> out += buildBitmap(ins)
            else -> {
                val expected = DrawBlob.expectedOperandCount(opcode)
                if (ins.args.size != expected) {
                    error("图元 '${ins.name}' 期望 $expected 个参数，实际 ${ins.args.size}")
                }
                val operands = Array(ins.args.size) { i -> toOperand(ins.args[i]) }
                out += Instruction(opcode, operands)
            }
        }
    }

    /** `text(x, y, size, color, content)`：最后一个参数必须是字符串字面量。 */
    private fun buildText(ins: DrawInstructionAst.Primitive): Instruction {
        if (ins.args.size != 5) {
            error("图元 'text' 期望 5 个参数（x, y, size, color, content），实际 ${ins.args.size}")
        }
        val operands = arrayOfNulls<Operand>(5)
        for (i in 0 until 4) operands[i] = toOperand(ins.args[i])
        operands[4] = expectStrLiteral(ins.args[4], "text.content")
        @Suppress("UNCHECKED_CAST")
        return Instruction(DrawBlob.OP_TEXT, operands as Array<Operand>)
    }

    /** `drawImage(x, y, w, h, resourceId)`：最后一个参数必须是字符串字面量。 */
    private fun buildDrawImage(ins: DrawInstructionAst.Primitive): Instruction {
        if (ins.args.size != 5) {
            error("图元 'drawImage' 期望 5 个参数（x, y, w, h, resourceId），实际 ${ins.args.size}")
        }
        val operands = arrayOfNulls<Operand>(5)
        for (i in 0 until 4) operands[i] = toOperand(ins.args[i])
        operands[4] = expectStrLiteral(ins.args[4], "drawImage.resourceId")
        @Suppress("UNCHECKED_CAST")
        return Instruction(DrawBlob.OP_DRAW_IMAGE, operands as Array<Operand>)
    }

    /** `polygon(x0, y0, x1, y1, ..., xN-1, yN-1, color)`：至少 3 个顶点，参数数=2N+1。 */
    private fun buildPolygon(ins: DrawInstructionAst.Primitive): Instruction {
        if (ins.args.size < 7 || (ins.args.size - 1) % 2 != 0) {
            error("图元 'polygon' 参数数非法（要求 2N+1 且 N>=3），实际 ${ins.args.size}")
        }
        val operands = Array(ins.args.size) { i -> toOperand(ins.args[i]) }
        return Instruction(DrawBlob.OP_POLYGON, operands)
    }

    /**
     * `path(x0, y0, ..., xN-1, yN-1, width, color [, closed])`。
     * 末尾参数可为布尔字面量 closed；若省略则 closed = false。
     */
    private fun buildPath(ins: DrawInstructionAst.Primitive): Instruction {
        val args = ins.args
        // 先判定末位是否为 closed 布尔字面量
        val lastIsClosed = args.isNotEmpty() && args.last() is ExprAst.Literal.BoolLit
        val closed = if (lastIsClosed) (args.last() as ExprAst.Literal.BoolLit).value else false
        val numericArgs = if (lastIsClosed) args.subList(0, args.size - 1) else args
        // 剩余参数 = 2N + 2（width/color）；N >= 2
        if (numericArgs.size < 6 || (numericArgs.size - 2) % 2 != 0) {
            error("图元 'path' 参数数非法（要求 2N+2[+closed] 且 N>=2），实际 ${args.size}")
        }
        val operands = Array(numericArgs.size) { i -> toOperand(numericArgs[i]) }
        return Instruction(DrawBlob.OP_PATH, operands, closed)
    }

    /**
     * v4 B.4：`pathBuild(cmdsList, mode, width, color)` —— 编译 OP_PATH_BUILD。
     *
     * - `cmdsList`：必须是 `ListLiteralExpr`，元素须为 `moveTo / lineTo / quadTo / cubicTo / close` CallExpr
     * - `mode`：必须是数值字面量 0/1/2（stroke / fill_nonzero / fill_evenodd）
     * - `width` / `color`：与其它图元一致，支持 EXPR / LITERAL
     */
    private fun buildPathBuild(ins: DrawInstructionAst.Primitive): Instruction {
        val args = ins.args
        if (args.size != 4) {
            error("图元 'pathBuild' 期望 4 个参数（cmds, mode, width, color），实际 ${args.size}")
        }
        val listExpr = args[0]
        if (listExpr !is ExprAst.ListLiteralExpr) {
            error("图元 'pathBuild' 第 1 参数必须是 PathCommand 节点列表字面量 [moveTo(...), ...]，实际 ${listExpr::class.simpleName}")
        }
        if (listExpr.elements.isEmpty()) {
            error("图元 'pathBuild' 节点列表至少含一个 moveTo 节点")
        }
        val kinds = ByteArray(listExpr.elements.size)
        val coordOperands = mutableListOf<Operand>()
        for ((idx, element) in listExpr.elements.withIndex()) {
            if (element !is ExprAst.CallExpr) {
                error("pathBuild 节点列表第 ${idx + 1} 项必须是节点构造器调用（moveTo/lineTo/quadTo/cubicTo/close），实际 ${element::class.simpleName}")
            }
            val callee = element.callee
            val nodeName = when (callee) {
                is ExprAst.IdentRef -> callee.name
                is ExprAst.LocalRef -> callee.name
                else -> error("pathBuild 节点列表第 ${idx + 1} 项 callee 必须是裸标识符，实际 ${callee::class.simpleName}")
            }
            val (kindByte, expectedArity) = when (nodeName) {
                "moveTo" -> DrawOpcodes.PATH_NODE_MOVE_TO to 2
                "lineTo" -> DrawOpcodes.PATH_NODE_LINE_TO to 2
                "quadTo" -> DrawOpcodes.PATH_NODE_QUAD_TO to 4
                "cubicTo" -> DrawOpcodes.PATH_NODE_CUBIC_TO to 6
                "close" -> DrawOpcodes.PATH_NODE_CLOSE to 0
                else -> error("pathBuild 节点列表第 ${idx + 1} 项未知节点 '$nodeName'；合法集合=${top.wcpe.allincore.dsl.compiler.parse.DrawBlockParser.PATH_NODE_NAMES}")
            }
            if (element.args.size != expectedArity) {
                error("pathBuild 节点 '$nodeName' 期望 $expectedArity 个实参，实际 ${element.args.size}")
            }
            if (idx == 0 && nodeName != "moveTo") {
                error("pathBuild 节点列表首项必须是 moveTo，实际 '$nodeName'")
            }
            kinds[idx] = kindByte
            for (a in element.args) {
                coordOperands += toOperand(a)
            }
        }
        // mode 必须是 0/1/2 数值字面量
        val modeArg = args[1]
        val mode = when (modeArg) {
            is ExprAst.Literal.NumberLit -> modeArg.value.toInt()
            else -> error("pathBuild 第 2 参数 mode 必须是数值字面量 0/1/2，实际 ${modeArg::class.simpleName}")
        }
        if (mode !in 0..2) {
            error("pathBuild mode 必须是 0(stroke)/1(fill_nonzero)/2(fill_evenodd)，实际 $mode")
        }
        // width 与 color：与其它图元一致经 toOperand 翻译，支持 EXPR / LITERAL
        val widthOperand = toOperand(args[2])
        val colorOperand = toOperand(args[3])
        val operands = Array(coordOperands.size + 2) { i ->
            when {
                i < coordOperands.size -> coordOperands[i]
                i == coordOperands.size -> widthOperand
                else -> colorOperand
            }
        }
        return Instruction(DrawBlob.OP_PATH_BUILD, operands, kinds, mode.toByte())
    }

    /**
     * v4 B.5：`bitmap(data, srcW, srcH, paletteId, dstX, dstY, dstW, dstH)` —— 编译 OP_BITMAP。
     *
     * - `data`：必须是 `ListLiteralExpr`，元素须为 `NumberLit` 整数字面量（取低 8 位作为 byte）
     * - 其余 7 个参数：与其它图元一致，支持 EXPR / LITERAL
     *
     * <p>本 Stage 仅支撑字面量字节数组路径；运行期动态 byte 数组（state.bytes 等）由后续 Stage
     * 引入 byteDataPool 后再支持。</p>
     */
    private fun buildBitmap(ins: DrawInstructionAst.Primitive): Instruction {
        val args = ins.args
        if (args.size != 8) {
            error("图元 'bitmap' 期望 8 个参数（data, srcW, srcH, paletteId, dstX, dstY, dstW, dstH），实际 ${args.size}")
        }
        val dataExpr = args[0]
        if (dataExpr !is ExprAst.ListLiteralExpr) {
            error("图元 'bitmap' 第 1 参数 data 必须是 [...] 字节字面量列表，实际 ${dataExpr::class.simpleName}")
        }
        val bytes = ByteArray(dataExpr.elements.size)
        for ((i, e) in dataExpr.elements.withIndex()) {
            if (e !is ExprAst.Literal.NumberLit) {
                error("bitmap data[$i] 必须是数字字面量，实际 ${e::class.simpleName}")
            }
            // 取低 8 位作为 byte（byte 在 JVM 上是 signed -128..127；这里允许 0..255 输入）
            bytes[i] = e.value.toInt().toByte()
        }
        val operands = arrayOf<Operand>(
            Operand.bytes(bytes),
            toOperand(args[1]),
            toOperand(args[2]),
            toOperand(args[3]),
            toOperand(args[4]),
            toOperand(args[5]),
            toOperand(args[6]),
            toOperand(args[7]),
        )
        return Instruction(DrawBlob.OP_BITMAP, operands)
    }

    /** 期望 [expr] 为字符串字面量，否则报错。 */
    private fun expectStrLiteral(expr: ExprAst, hint: String): Operand {
        if (expr !is ExprAst.Literal.StringLit) {
            error("'$hint' 必须是字符串字面量，实际为 ${expr::class.simpleName}")
        }
        return Operand.str(expr.value)
    }

    /**
     * 将 [ExprAst] 编译为 [Operand]：
     *  - 数值字面量 → LITERAL；
     *  - 布尔字面量 → LITERAL(0.0/1.0)；
     *  - 字符串字面量在数值位置上仅允许 `#RRGGBB` / `#AARRGGBB` 形式的颜色，编译为 ARGB int 字面量；
     *    其它字符串字面量抛错；
     *  - IdentRef 若命中已分配 slot 的 val/for 变量，替换为 LocalRef("$slotN")；
     *  - 其它（引用、BinaryOp、插值 …）序列化为 ExprBlob → EXPR。
     */
    private fun toOperand(expr: ExprAst): Operand = when (expr) {
        is ExprAst.Literal.NumberLit -> Operand.literal(expr.value.toDouble())
        is ExprAst.Literal.BoolLit -> Operand.literal(if (expr.value) 1.0 else 0.0)
        is ExprAst.Literal.StringLit -> {
            // RFC §3.2：draw 块内颜色参数接受 #RRGGBB / #AARRGGBB 十六进制字面量
            val argb = parseHexColor(expr.value)
                ?: error("数值位置不允许字符串字面量 '${expr.value}'（仅允许 #RRGGBB / #AARRGGBB 颜色字面量）")
            Operand.literal(argb.toDouble())
        }
        else -> Operand.expr(ExprSerializer.serializeExpr(rewriteSlotRefs(expr)))
    }

    /**
     * 递归替换表达式中引用已分配 slot 的 val/for 变量名（IdentRef）为 LocalRef("$slotN")，
     * 使运行时 ExprEvaluator 能通过 EvalContext.getVar("$slotN") 正确取值。
     */
    private fun rewriteSlotRefs(expr: ExprAst): ExprAst = when (expr) {
        is ExprAst.IdentRef -> {
            val slot = currentSlots?.lookup(expr.name)
            if (slot != null) ExprAst.LocalRef("\$slot$slot", expr.position)
            else expr
        }
        is ExprAst.BinaryOp -> expr.copy(
            left = rewriteSlotRefs(expr.left),
            right = rewriteSlotRefs(expr.right),
        )
        is ExprAst.UnaryOp -> expr.copy(operand = rewriteSlotRefs(expr.operand))
        is ExprAst.CallExpr -> expr.copy(
            callee = rewriteSlotRefs(expr.callee),
            args = expr.args.map { rewriteSlotRefs(it) },
        )
        is ExprAst.BuiltinCallExpr -> expr.copy(args = expr.args.map { rewriteSlotRefs(it) })
        is ExprAst.MemberAccess -> expr.copy(target = rewriteSlotRefs(expr.target))
        is ExprAst.MemberCall -> expr.copy(
            target = rewriteSlotRefs(expr.target),
            args = expr.args.map { rewriteSlotRefs(it) },
            namedArgs = expr.namedArgs.map { it.copy(value = rewriteSlotRefs(it.value)) },
        )
        // 叶子节点或不含 IdentRef 的节点直接返回
        else -> expr
    }

    /**
     * 解析 `#RRGGBB` 或 `#AARRGGBB` 为 ARGB int；非法格式返回 null。
     * - 6 位 → alpha 补 0xFF
     * - 8 位 → 直接按 AARRGGBB 解析
     */
    private fun parseHexColor(s: String): Int? {
        if (!s.startsWith("#")) return null
        val hex = s.substring(1)
        return try {
            when (hex.length) {
                6 -> 0xFF000000.toInt() or hex.toInt(16)
                8 -> hex.toLong(16).toInt()
                else -> null
            }
        } catch (_: NumberFormatException) {
            null
        }
    }

    /** 翻译 `val name = expr`：分配 slot 并发射 VAL_ASSIGN。 */
    private fun emitValAssign(
        ins: DrawInstructionAst.ValAssign,
        out: MutableList<Instruction>,
        slots: SlotAllocator,
    ) {
        val slot = slots.allocate(ins.name)
        val operand = toOperand(ins.value)
        out += DrawBlob.valAssign(slot, operand)
    }

    /**
     * 翻译 `for (v in from until to) { body }`：
     *  - 将 body 在独立列表中递归编码，以计算 bodyByteLength；
     *  - 为循环变量 v 单独开启一个子作用域，退出时自动回收。
     */
    private fun emitFor(
        ins: DrawInstructionAst.For,
        out: MutableList<Instruction>,
        slots: SlotAllocator,
    ) {
        val iterCount = ins.to - ins.from
        if (iterCount < 0) error("for 上界 ${ins.to} 不得小于下界 ${ins.from}")

        val scope = slots.pushScope()
        val slot = slots.allocate(ins.varName)

        val bodyInstructions = mutableListOf<Instruction>()
        emitInstructions(ins.body, bodyInstructions, slots)

        slots.popScope(scope)

        val bodyByteLength = bodyInstructions.sumOf { DrawBlob.instructionByteLength(it) }

        out += DrawBlob.forBegin(slot, iterCount, bodyByteLength)
        out += bodyInstructions
        out += DrawBlob.forEnd()
    }

    /** 翻译 `if (cond) { then } else { elseBranch }`：装配 IF_BEGIN + then + IF_END + else + IF_END。 */
    private fun emitIfElse(
        ins: DrawInstructionAst.IfElse,
        out: MutableList<Instruction>,
        slots: SlotAllocator,
    ) {
        val conditionOp = toOperand(ins.condition)

        val thenScope = slots.pushScope()
        val thenInstructions = mutableListOf<Instruction>()
        emitInstructions(ins.thenBranch, thenInstructions, slots)
        slots.popScope(thenScope)
        val thenByteLength = thenInstructions.sumOf { DrawBlob.instructionByteLength(it) }

        val elseScope = slots.pushScope()
        val elseInstructions = mutableListOf<Instruction>()
        emitInstructions(ins.elseBranch, elseInstructions, slots)
        slots.popScope(elseScope)
        val elseByteLength = elseInstructions.sumOf { DrawBlob.instructionByteLength(it) }

        out += DrawBlob.ifBegin(conditionOp, thenByteLength, elseByteLength)
        out += thenInstructions
        out += DrawBlob.ifEnd()
        out += elseInstructions
        out += DrawBlob.ifEnd()
    }
}

/** 局部变量 slot 分配器（栈式作用域，最多 [DrawBlob.LOCAL_SLOT_COUNT] 个同时活跃）。 */
private class SlotAllocator {
    private val varSlots = mutableMapOf<String, Int>()
    private var nextSlot = 0

    /** 作用域快照：pushScope 返回后，popScope 可据此恢复。 */
    data class Scope(val savedNextSlot: Int, val savedVars: Map<String, Int>)

    fun pushScope(): Scope = Scope(nextSlot, HashMap(varSlots))

    fun popScope(scope: Scope) {
        nextSlot = scope.savedNextSlot
        varSlots.clear()
        varSlots.putAll(scope.savedVars)
    }

    /** 为 [name] 分配一个新 slot；超过上限时抛错。 */
    fun allocate(name: String): Int {
        if (nextSlot >= DrawBlob.LOCAL_SLOT_COUNT) {
            error("局部变量数超过 ${DrawBlob.LOCAL_SLOT_COUNT}：$name")
        }
        val slot = nextSlot++
        varSlots[name] = slot
        return slot
    }

    /** 查找变量名对应的 slot 索引；未分配返回 null。 */
    fun lookup(name: String): Int? = varSlots[name]
}
